import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OptionSelect, RefSelect } from "@/components/data/FieldInput";
import { api, errMsg, fmtMoney, fmtNum, today } from "@/lib/api";

const MOYEN = { name: "moyen_paiement_id", type: "refitem", ref: "moyen_paiement" };
const COMPTE = { name: "compte_id", type: "ref", ref: "comptes" };
const EMPTY = { superviseur_id: null, montant: "", moyen_paiement_id: null, compte_id: null, reference: "", date: today() };

export function EncaissementDialog({ open, onOpenChange, journee, superviseurs, onDone }) {
  const [f, setF] = useState(EMPTY);
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setF(EMPTY); setRep(null); }
  }, [open]);
  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); if (k === "superviseur_id" || k === "montant") setRep(null); };

  const calculer = async () => {
    try {
      const r = await api.post(`/tfj/journees/${journee.id}/repartition`, { superviseur_id: f.superviseur_id, montant: f.montant });
      const prop = Object.fromEntries(r.data.proposition.map((p) => [p.revendeur_id, p.montant]));
      setRep(r.data.revendeurs.map((x) => ({ ...x, part: prop[x.revendeur_id] || 0 })));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const somme = (rep || []).reduce((s, x) => s + Number(x.part || 0), 0);
  const ok = rep && Math.abs(somme - Number(f.montant || 0)) < 0.5;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/tfj/journees/${journee.id}/encaissements`, { ...f, repartition: rep.map((x) => ({ revendeur_id: x.revendeur_id, montant: x.part })) });
      toast.success("Encaissement enregistré et comptabilisé en trésorerie");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const sup = superviseurs.find((s) => s.cle === f.superviseur_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="encaissement-dialog">
        <DialogHeader>
          <DialogTitle>Encaissement superviseur — journée {journee.date}</DialogTitle>
          <DialogDescription>Le montant n'est considéré comme encaissé qu'après enregistrement : une entrée est passée sur le compte de trésorerie choisi.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2"><Label>Superviseur *</Label><div className="mt-1"><OptionSelect options={superviseurs.map((s) => ({ value: s.cle, label: `${s.libelle} — reste ${fmtNum(s.reste)}` }))} value={f.superviseur_id} onChange={(v) => set("superviseur_id", v)} testId="enc-superviseur-select" /></div></div>
          <div><Label>Montant reçu *</Label><Input type="number" className="font-mono mt-1" value={f.montant} onChange={(e) => set("montant", e.target.value)} data-testid="enc-montant-input" /></div>
          <div><Label>Moyen de paiement *</Label><div className="mt-1"><RefSelect field={MOYEN} value={f.moyen_paiement_id} onChange={(v) => set("moyen_paiement_id", v)} testId="enc-moyen-select" /></div></div>
          <div><Label>Compte de trésorerie *</Label><div className="mt-1"><RefSelect field={COMPTE} value={f.compte_id} onChange={(v) => set("compte_id", v)} testId="enc-compte-select" /></div></div>
          <div><Label>Date de paiement</Label><Input type="date" className="mt-1" value={f.date} onChange={(e) => set("date", e.target.value)} /></div>
          <div className="sm:col-span-3"><Label>Référence (bordereau, transaction…)</Label><Input className="mt-1" value={f.reference} onChange={(e) => set("reference", e.target.value)} data-testid="enc-reference-input" /></div>
        </div>
        {sup && <div className="text-xs text-muted-foreground">Attendu du jour {fmtMoney(sup.du)} · Dette antérieure {fmtMoney(sup.dette_anterieure)} · Déjà encaissé {fmtMoney(sup.encaisse)} · Reste dû {fmtMoney(sup.reste)}</div>}
        <Button variant="outline" onClick={calculer} disabled={!f.superviseur_id || !(Number(f.montant) > 0)} data-testid="enc-calculer-button">Calculer la répartition entre les revendeurs</Button>
        {rep && (
          <div className="border rounded-md overflow-x-auto">
            <table className="w-full text-xs" data-testid="enc-repartition-table">
              <thead className="bg-muted/60"><tr><th className="p-2 text-left">Revendeur</th><th className="p-2 text-right">Attendu</th><th className="p-2 text-right">Dette ant.</th><th className="p-2 text-right">Déjà encaissé</th><th className="p-2 text-right w-40">Part affectée</th></tr></thead>
              <tbody>
                {rep.map((x, i) => (
                  <tr key={x.revendeur_id} className="border-t">
                    <td className="p-2">{x.revendeur_label}</td>
                    <td className="p-2 text-right font-mono">{fmtNum(x.du)}</td>
                    <td className="p-2 text-right font-mono">{fmtNum(x.dette_anterieure)}</td>
                    <td className="p-2 text-right font-mono">{fmtNum(x.encaisse)}</td>
                    <td className="p-1"><Input type="number" className="h-8 font-mono text-right" value={x.part} data-testid={`enc-part-${i}`}
                      onChange={(e) => setRep(rep.map((y, j) => (j === i ? { ...y, part: e.target.value } : y)))} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t font-semibold"><td className="p-2" colSpan={4}>Total réparti</td><td className={`p-2 text-right font-mono ${ok ? "text-emerald-700" : "text-primary"}`} data-testid="enc-somme">{fmtNum(somme)} / {fmtNum(f.montant)}</td></tr></tfoot>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button onClick={submit} disabled={!ok || busy || !f.moyen_paiement_id || !f.compte_id} data-testid="enc-submit-button">{busy ? "Enregistrement…" : "Enregistrer l'encaissement"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
